import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    FiltersSelectionWrapper,
    PanelHeader,
    PanelWrapper,
    PeriodSelectionWrapper,
    SetButton,
    UpperWrapper,
    Wrapper,
} from './ReportAolFilterPanel.styles';
import { Checkbox } from 'antd';
import { useAolStore } from '../../../context/AolContext';
import { useAppStore } from '../../../context/AppContext';
import { useVesselStore } from '../../../context/VesselContext';
import { FilterButton } from '../../../styles/button.style';
import useGetAolData from '../../../api/aol/useGetAolData';
import { Period } from '../../../enums/PeriodEnum';
import { useUserSettingsStore } from '../../../context/UserSettingsContext';
import useUserSettingsApi from '../../../api/useUserSettingsApi';
import { WarningText } from '../../shared/warning-text/WarningText.style';
import { formatAolUrlDate } from '../../../utils/aol/aolDateUtils';
import OdfjellDatePicker from '../../common/date-picker/OdfjellDatePicker';
import { PageType } from '../../../types/Page';
import {
    subtractDateDays,
    getDateNow,
    addDateDays,
    getDiffBetweenDates,
    isDateValid,
    getLuxonDateTime,
    isDateAfter,
} from 'odfjell-date-time-utils/dateTimeUtils';

const { RangePicker } = OdfjellDatePicker;

const MAX_DATE_RANGE_DAYS = 60;

type Props = {
    page: PageType;
    onChange: (fromDate: Date, toDate: Date) => void;
};

const ReportAolFilterPanel: React.FC<Props> = (props: Props) => {
    const { aolFilter, period, setPeriod, setDefaultAolFilter } = useAolStore();
    const { isReportAolFilterPanelOpen, setIsReportAolFilterPanelOpen } = useAppStore();
    const { getAolData } = useGetAolData();
    const { userSettings } = useUserSettingsStore();
    const { upsertUserSettings } = useUserSettingsApi();

    const { selectedVessel } = useVesselStore();
    const toggleFilterPanel = () => {
        setIsReportAolFilterPanelOpen(!isReportAolFilterPanelOpen);
    };
    const [dateRange, setDateRange] = useState<[Date, Date]>([subtractDateDays(getDateNow(), 14), getDateNow()]);
    const [limitedRange, setLimitedRange] = useState<boolean>(false);

    const wrapperRef = useRef(null);

    const DetectClickOutside = (ref) => {
        useEffect(() => {
            const handleClickOutside = (event) => {
                if (!ref.current) return;
                if (
                    (ref.current && ref.current.contains(event.target)) ||
                    (event.target?.className && typeof event.target.className === 'string' && event.target?.className?.includes('ant-picker'))
                ) {
                    return;
                }
                setIsReportAolFilterPanelOpen(false);
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                // Unbind the event listener on clean up
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [ref]);
    };
    DetectClickOutside(wrapperRef);

    useEffect(() => {
        if (aolFilter?.fromDate && aolFilter?.toDate) {
            setDateRange([aolFilter.fromDate, aolFilter.toDate]);
        }
    }, [aolFilter]);

    const isFilterPanelActive = () => {
        return isReportAolFilterPanelOpen ? true : false;
    };

    const aolCalendarFilterDate = (date?: Date) => {
        if (date) {
            return date;
        }
        return aolFilter?.fromDate ? aolFilter.fromDate : getDateNow();
    };

    const getToDate = (fromDate: Date): Date => {
        const toDate = addDateDays(fromDate, period === Period.TwoWeeks ? 14 : 28);

        if (isDateAfterMaxValidDate(toDate)) {
            return addDateDays(getDateNow(), 1);
        }

        return toDate;
    };
    const isDateAfterMaxValidDate = (date: Date) => {
        return isDateAfter(date, addDateDays(getDateNow(), 1), 'day');
    };

    const onAolFilterDateChange = (filterDate: Date | null) => {
        if (filterDate && selectedVessel) {
            const aolToDate = getToDate(filterDate);
            props.onChange(filterDate, aolToDate);
            setIsReportAolFilterPanelOpen(false);
        }
    };

    const changePeriod = (clickedPeriod: Period) => {
        if (period !== clickedPeriod) {
            setPeriod(clickedPeriod);
            if (clickedPeriod !== Period.Custom) {
                upsertUserSettings({ ...userSettings, aolPeriod: clickedPeriod });
                setDefaultAolFilter({
                    fromDate: subtractDateDays(getDateNow(), clickedPeriod === Period.TwoWeeks ? 14 : 28),
                    toDate: new Date(),
                    isDefault: true,
                });
            }
        }
    };

    useEffect(() => {
        const daysDiff = getDiffBetweenDates(dateRange[1], dateRange[0], 'day').days;
        if (daysDiff > MAX_DATE_RANGE_DAYS && !limitedRange) {
            setDateRange([dateRange[0], addDateDays(dateRange[0], MAX_DATE_RANGE_DAYS)]);
            setLimitedRange(true);
        }
    }, [dateRange, limitedRange]);

    const changeDateRange = (range: [Date, Date]) => {
        const daysDiff = getDiffBetweenDates(range[1], range[0], 'day').days;
        if (daysDiff <= MAX_DATE_RANGE_DAYS) {
            setLimitedRange(false);
        }
        setDateRange(range);
    };

    const applyCustomDateRange = () => {
        if (!dateRange || !isDateValid(dateRange?.[0]) || !isDateValid(dateRange?.[1])) return;
        props.onChange(...dateRange);
        setIsReportAolFilterPanelOpen(false);
        getAolData(selectedVessel.imoNumber, formatAolUrlDate(dateRange[0]), formatAolUrlDate(dateRange[1]));
    };

    return (
        <Wrapper>
            <UpperWrapper>
                <FiltersSelectionWrapper>
                    <FilterButton isSelected={isFilterPanelActive()} onClick={toggleFilterPanel} title="Open AOL dates filter">
                        <FontAwesomeIcon icon="filter" />
                    </FilterButton>
                </FiltersSelectionWrapper>
            </UpperWrapper>
            {isReportAolFilterPanelOpen && (
                <PanelWrapper customDateRange={period === Period.Custom} ref={wrapperRef}>
                    <PanelHeader>Change Aol filter date</PanelHeader>
                    <PeriodSelectionWrapper>
                        <Checkbox key="check_period_1" checked={period === Period.TwoWeeks} onClick={() => changePeriod(Period.TwoWeeks)}>
                            2 Weeks
                        </Checkbox>
                        <Checkbox key="check_period_2" checked={period === Period.FourWeeks} onClick={() => changePeriod(Period.FourWeeks)}>
                            4 Weeks
                        </Checkbox>
                        <Checkbox key="check_period_3" checked={period === Period.Custom} onClick={() => changePeriod(Period.Custom)}>
                            Custom
                        </Checkbox>
                    </PeriodSelectionWrapper>
                    {period === Period.Custom ? (
                        <div>
                            <RangePicker
                                open={true}
                                format="DD.MM.YYYY"
                                disabledDate={(current) => current && isDateAfterMaxValidDate(current.toJSDate())}
                                value={[getLuxonDateTime(dateRange[0]), getLuxonDateTime(dateRange[1])]}
                                onCalendarChange={(range) => changeDateRange([range[0].toJSDate(), range[1].toJSDate()])}
                                clearIcon={false}
                                renderExtraFooter={() =>
                                    limitedRange && (
                                        <WarningText className="ant-picker-custom-footer" style={{ marginTop: 0 }}>
                                            Maximum range is {MAX_DATE_RANGE_DAYS} days
                                        </WarningText>
                                    )
                                }
                            />
                            <SetButton disabled={!isDateValid(dateRange?.[0]) || !isDateValid(dateRange?.[1])} onClick={() => applyCustomDateRange()}>
                                <FontAwesomeIcon icon="check" title="Set" />
                            </SetButton>
                        </div>
                    ) : (
                        <OdfjellDatePicker
                            className="aol-filter-date-picker"
                            open={true}
                            disabledDate={(current) => current && isDateAfterMaxValidDate(current.toJSDate())}
                            format={'DD.MM.YYYY'}
                            clearIcon={false}
                            value={getLuxonDateTime(aolCalendarFilterDate())}
                            placeholder={''}
                            onChange={(date) => onAolFilterDateChange(date.toJSDate())}
                        />
                    )}
                </PanelWrapper>
            )}
        </Wrapper>
    );
};
export default ReportAolFilterPanel;
